import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmt } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist } = useAuth();
  const [p, setP] = useState(null);
  const [sizeIdx, setSizeIdx] = useState(0);
  const [color, setColor] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setSizeIdx(0);
      setColor(r.data.colors?.[0] || null);
    }).catch(() => nav("/shop"));
  }, [slug, nav]);

  if (!p) return <div className="pt-40 text-center caption text-sm">Loading…</div>;

  const price = p.sizes?.[sizeIdx]?.price ?? p.price;

  const add = () => {
    addItem({
      product_id: p.id, name: p.name, size: p.sizes?.[sizeIdx]?.label,
      color, price, quantity: 1, image: p.image,
    });
    toast(`${p.name} added to bag`);
  };

  return (
    <div data-testid="product-detail-page" className="pt-16">
      <div className="px-6 md:px-10 lg:px-20 pt-10 pb-24">
        <button onClick={() => nav(-1)} data-testid="pdp-back" className="util-link text-[11px] link-hover mb-8">← Back</button>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* IMAGE */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-24 self-start space-y-6">
            <div className="w-full aspect-[4/5] bg-card overflow-hidden">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
            </div>
            {p.image2 && (
              <div className="w-full aspect-[4/5] bg-card overflow-hidden hidden lg:block">
                <img src={p.image2} alt={p.name} className="w-full h-full object-cover" />
              </div>
            )}
          </motion.div>

          {/* DETAILS */}
          <div className="lg:pt-4">
            <p className="util-link text-[11px] caption mb-4">{p.subcategory}</p>
            <h1 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">{p.name}</h1>
            <p className="caption text-sm mt-4">{p.tagline}</p>
            <div className="text-2xl mt-8">{fmt(price)}</div>

            {/* MATERIAL + STATIC FEATURES */}
            <div className="mt-12 border-t border-border pt-10">
              <p className="util-link text-[11px] caption mb-2">Material</p>
              <p className="text-lg mb-10">{p.material}</p>

              <p className="util-link text-[11px] caption mb-4">Features & Benefits</p>
              <ul data-testid="feature-list" className="space-y-2.5 pl-4">
                {p.features.map((f, i) => (
                  <li key={i} data-testid={`feature-${i}`} className="text-[14px] leading-relaxed list-disc marker:text-black text-black/85">
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* OPTIONS */}
            <div className="mt-12">
              <p className="util-link text-[11px] caption mb-4">Size</p>
              <div className="flex flex-wrap gap-6">
                {p.sizes.map((s, i) => (
                  <button key={s.label} data-testid={`size-${i}`} onClick={() => setSizeIdx(i)}
                    className={`text-[13px] link-hover pb-1 ${sizeIdx === i ? "border-b border-black" : "text-graphite"}`}>
                    {s.label} · {fmt(s.price)}
                  </button>
                ))}
              </div>
            </div>

            {p.colors?.length > 0 && (
              <div className="mt-8">
                <p className="util-link text-[11px] caption mb-4">Colour</p>
                <div className="flex flex-wrap gap-6">
                  {p.colors.map((c) => (
                    <button key={c} data-testid={`color-${c}`} onClick={() => setColor(c)}
                      className={`text-[13px] link-hover pb-1 ${color === c ? "border-b border-black" : "text-graphite"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button data-testid="pdp-add-to-cart" onClick={add}
              className="w-full bg-black text-white py-4 mt-12 product-title text-[13px] link-hover">
              Add to Bag — {fmt(price)}
            </button>

            <button data-testid="pdp-wishlist"
              onClick={async () => {
                const r = await toggleWishlist(p.id);
                if (r === "unauth") { toast("Log in to save to your wishlist"); nav("/login"); }
                else toast(isWishlisted(p.id) ? "Saved to wishlist" : "Removed from wishlist");
              }}
              className="w-full border border-black py-4 mt-3 product-title text-[13px] link-hover flex items-center justify-center gap-2">
              <Heart className="w-4 h-4" strokeWidth={1.5} fill={isWishlisted(p.id) ? "#000" : "none"} />
              {isWishlisted(p.id) ? "Saved to Wishlist" : "Add to Wishlist"}
            </button>

            <div className="grid grid-cols-2 gap-4 mt-6 caption text-[12px]">
              <div>Free shipping over $150</div>
              <div>30-day easy returns</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
